import React, { useState, useRef } from 'react';
import dataExportService, { UserDataExport } from '../services/data-export';
import './DataExportModal.css';

interface DataExportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const DataExportModal: React.FC<DataExportModalProps> = ({ isOpen, onClose }) => {
  const [exportFormat, setExportFormat] = useState<'json' | 'csv'>('json');
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [restoreConfirmData, setRestoreConfirmData] = useState<{ date: string; records: number; data: UserDataExport } | null>(null);
  const [restoreMessage, setRestoreMessage] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleExport = async () => {
    try {
      setIsExporting(true);
      setError(null);
      setExportProgress(0);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setExportProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      if (exportFormat === 'json') {
        await dataExportService.exportDataJSON();
      } else {
        await dataExportService.exportDataCSV();
      }

      clearInterval(progressInterval);
      setExportProgress(100);
      setSuccess(true);

      setTimeout(() => {
        onClose();
        setSuccess(false);
        setExportProgress(0);
      }, 1500);
    } catch (error: any) {
      setError(error.message || 'Failed to export data. Please try again.');
      setExportProgress(0);
    } finally {
      setIsExporting(false);
    }
  };

  const handleRestore = async () => {
    if (!fileInputRef.current) return;

    fileInputRef.current.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsExporting(true);
      setError(null);

      const backupData = await dataExportService.loadBackupFile(file);
      setRestoreConfirmData({
        date: backupData.metadata.exportDate,
        records: backupData.metadata.totalRecords,
        data: backupData,
      });
      setIsExporting(false);
    } catch (error: any) {
      setError(error.message || 'Failed to restore data. Please check the backup file format.');
      setIsExporting(false);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const confirmRestore = async () => {
    if (!restoreConfirmData) return;
    try {
      setIsExporting(true);
      await dataExportService.restoreData(restoreConfirmData.data);
      setRestoreMessage('Data restore initiated. This feature is coming soon!');
      setRestoreConfirmData(null);
      setTimeout(() => {
        setRestoreMessage('');
        onClose();
      }, 3000);
    } catch (error: any) {
      setError(error.message || 'Failed to restore data.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="data-export-modal-overlay" onClick={onClose}>
      <div className="data-export-modal" onClick={(e) => e.stopPropagation()}>
        <div className="data-export-modal-header">
          <h2>Data Export & Backup</h2>
          <button className="data-export-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="data-export-modal-content">
          {error && (
            <div className="data-export-error">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="data-export-success">
              <span>✓</span>
              <span>Export completed successfully!</span>
            </div>
          )}

          <div className="data-export-section">
            <h3>Export Your Data</h3>
            <p className="data-export-description">
              Download a complete backup of all your data. Choose your preferred format:
            </p>

            <div className="data-export-format-selector">
              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="json"
                  checked={exportFormat === 'json'}
                  onChange={() => setExportFormat('json')}
                  disabled={isExporting}
                />
                <div className="format-option-content">
                  <strong>JSON</strong>
                  <span>Complete data with all details (recommended)</span>
                </div>
              </label>

              <label className="format-option">
                <input
                  type="radio"
                  name="format"
                  value="csv"
                  checked={exportFormat === 'csv'}
                  onChange={() => setExportFormat('csv')}
                  disabled={isExporting}
                />
                <div className="format-option-content">
                  <strong>CSV</strong>
                  <span>Spreadsheet-friendly format for analysis</span>
                </div>
              </label>
            </div>

            {isExporting && (
              <div className="data-export-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${exportProgress}%` }}
                  />
                </div>
                <span className="progress-text">{exportProgress}%</span>
              </div>
            )}

            <button
              className="btn btn-primary btn-export"
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? 'Exporting...' : `Export as ${exportFormat.toUpperCase()}`}
            </button>
          </div>

          <div className="data-export-divider" />

          <div className="data-export-section">
            <h3>Restore from Backup</h3>
            <p className="data-export-description">
              Restore your data from a previously exported backup file (JSON format).
            </p>
            <p className="data-export-warning">
              ⚠️ Note: Restore functionality is coming soon. For now, you can download your data for safekeeping.
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {restoreMessage && (
              <div className="data-export-success">
                <span>✓</span>
                <span>{restoreMessage}</span>
              </div>
            )}

            {restoreConfirmData ? (
              <div className="inline-confirm" style={{ flexWrap: 'wrap' }}>
                <span className="inline-confirm-label">
                  Restore from {restoreConfirmData.date} ({restoreConfirmData.records} records)? This may overwrite existing data.
                </span>
                <button className="inline-confirm-yes" onClick={confirmRestore}>Yes, Restore</button>
                <button className="inline-confirm-no" onClick={() => setRestoreConfirmData(null)}>Cancel</button>
              </div>
            ) : (
              <button
                className="btn btn-secondary btn-restore"
                onClick={handleRestore}
                disabled={isExporting}
              >
                Choose Backup File
              </button>
            )}
          </div>

          <div className="data-export-info">
            <p>
              <strong>What's included in the export:</strong>
            </p>
            <ul>
              <li>User profile and settings</li>
              <li>Journal entries</li>
              <li>Routines and completions</li>
              <li>Tasks and checklists</li>
              <li>Calendar events</li>
              <li>Health entries</li>
              <li>Vision board items</li>
              <li>Budget data (transactions, categories, savings goals, etc.)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataExportModal;

